import { clickToCopy } from './clipboard.ts';
import { $, must, text, withClass } from './dom.ts';
import type { TestCaseResult, TestResults } from './evaluator-types.ts';

/*
 * The per-case record the generic harnesses produce and this module renders:
 * pre-formatted strings, plus the pass/fail flag.
 */
type LabeledTestResult = {
  label: string;
  got: string;
  expected: string;
  passed: boolean;
};

/*
 * Turn a div we are given into a widget for displaying test results. Hacked
 * from testing.js to put the responsibility for formatting the results onto the
 * code that generates the result. We'll just take some strings and stick them
 * in a table except for the last one which says whether it passed of failed.
 * For now we'll stick with four columns but maybe even that should be left to
 * the result generator.
 */
const testing = (div: HTMLElement, casesTemplate: HTMLTemplateElement) =>
  new Testing(div, casesTemplate);

const makePills = (names: string[]) => {
  const pills = withClass('pills', $('<div>')) as HTMLDivElement;
  names.forEach((name) => {
    pills.append(pill(name));
  });
  return pills;
};

const pill = (name: string) => {
  const b = withClass('no_results', $('<button>', name)) as HTMLButtonElement;
  b.value = name;
  b.append(withClass('marker', $('<span>', '')) as HTMLSpanElement);
  return b;
};

const pillStyle = (results: TestCaseResult[] | null) => {
  if (results === null) {
    return { style: 'no_results', marker: '', allPassing: false };
  } else if (results.every((x) => x.passed)) {
    return { style: 'all_passing', marker: '✅', allPassing: true };
  } else {
    return { style: 'some_failing', marker: '❌', allPassing: false };
  }
};

const resultsTable = () => {
  const table = withClass('results', $('<table>')) as HTMLTableElement;
  table.hidden = true;

  const colgroup = $('<colgroup>') as HTMLElement;
  colgroup.append(withClass('label', $('<col>')) as HTMLElement);
  colgroup.append(withClass('got', $('<col>')) as HTMLElement);
  colgroup.append(withClass('expected', $('<col>')) as HTMLElement);
  colgroup.append(withClass('result', $('<col>')) as HTMLElement);
  table.append(colgroup);

  const thead = $('<thead>') as HTMLElement;
  const tr = $('<tr>') as HTMLElement;
  tr.append($('<th>', 'Label') as HTMLElement);
  tr.append($('<th>', 'Got') as HTMLElement);
  tr.append($('<th>', 'Expected') as HTMLElement);
  tr.append($('<th>', 'Passed?') as HTMLElement);
  thead.append(tr);
  table.append(thead);
  table.append($('<tbody>') as HTMLElement);
  return table;
};

const noResults = () => {
  const div = withClass('no-results', $('<div>', 'No test results yet.')) as HTMLDivElement;
  div.hidden = true;
  return div;
};

const resultsBody = (results: TestCaseResult[]) => {
  const tbody = $('<tbody>') as HTMLTableSectionElement;
  results.forEach((result) => addResultRow(tbody, result));
  return tbody;
};

const addResultRow = (tbody: HTMLTableSectionElement, result: TestCaseResult) => {
  const { label, got, expected, passed } = result as LabeledTestResult;
  const row = tbody.insertRow();
  row.className = passed ? 'pass' : 'fail';
  row.insertCell().append($('<pre>', label) as HTMLElement);
  row.insertCell().append($('<pre>', got) as HTMLElement);
  row.insertCell().append($('<pre>', expected) as HTMLElement);
  row.insertCell().append(text(passed ? '✅' : '❌'));
};

const functionDescription = (name: string, description: Element) => {
  const desc = withClass('description', $('<div>')) as HTMLDivElement;
  const prefix = $('<b>', `${name}: `) as HTMLElement;

  const ps = (description.cloneNode(true) as Element).children;
  ps[0].prepend(prefix);
  desc.replaceChildren(...ps);
  clickToCopy('code', desc);
  return desc;
};

class Testing {
  div: HTMLElement;
  descriptions: Record<string, Element>;
  pills: HTMLDivElement;
  description: HTMLElement;
  table: HTMLTableElement;
  noResults: HTMLElement;
  summary: HTMLElement;
  selected: string | undefined;
  allPassed: boolean;

  constructor(div: HTMLElement, casesTemplate: HTMLTemplateElement) {
    this.div = div;

    this.descriptions = Object.fromEntries(
      [...casesTemplate.content.children].map((c) => [
        must((c as HTMLElement).dataset.name, 'data-name on test case description'),
        c,
      ]),
    );
    this.pills = makePills(Object.keys(this.descriptions));

    this.description = withClass('description', $('<div>')) as HTMLDivElement;
    this.table = resultsTable();
    this.noResults = noResults();
    this.summary = withClass('summary', $('<div>')) as HTMLDivElement;

    this.div.append(this.pills);
    this.div.append(this.description);
    this.div.append(this.table);
    this.div.append(this.noResults);
    this.div.append(this.summary);

    this.selected = Object.keys(this.descriptions)[0];

    this.allPassed = false;
    clickToCopy('code', $('#instructions'));
  }

  update(testResults: TestResults) {
    this.allPassed = true;
    this.pills.querySelectorAll('button').forEach((b) => {
      const results = testResults[b.value] ?? null;
      b.classList.remove('no_results', 'some_failing', 'all_passing');

      const { style, marker, allPassing } = pillStyle(results);
      this.allPassed &&= allPassing;
      b.classList.add(style);

      // Changed when getting rid of whjqah. if it's broken now, that's probably why.
      must(b.querySelector('.marker'), 'pill marker').replaceChildren(text(marker));

      b.onclick = () => this.displayResults(b.value, results, true);
    });

    if (this.selected) {
      this.displayResults(this.selected, testResults[this.selected] ?? null);
    }
  }

  displayResults(name: string, results: TestCaseResult[] | null, copyName?: boolean) {
    this.selected = name;
    if (copyName) {
      navigator.clipboard.writeText(name);
    }

    const newDesc = functionDescription(name, this.descriptions[name]);
    this.description.replaceWith(newDesc);
    this.description = newDesc;

    if (results !== null) {
      const old = must(this.table.querySelector('tbody'), 'results table body');
      this.table.replaceChild(resultsBody(results), old);
      this.table.hidden = false;
      this.noResults.hidden = true;

      const passed = results.reduce((a, b) => a + (b.passed ? 1 : 0), 0);
      const all = passed === results.length ? 'All ' : '';
      const stop = passed === results.length ? '!' : '.';

      this.summary.replaceChildren(
        $('<p>', `${all}${passed} of ${results.length} test cases passed${stop}`) as HTMLElement,
      );
    } else {
      this.noResults.hidden = false;
      this.table.hidden = true;
      this.summary.hidden = true;
    }
  }
}

export default testing;
